/**
 * Theme context: light/dark toggle, persisted per-browser (localStorage key
 * `ft-theme`), default **light** (rechnungswerk-style flat light UI). Toggles
 * the `.dark` class on `<html>` so `index.css`'s `@custom-variant dark` and
 * every `dark:` utility class key on it instead of the OS
 * `prefers-color-scheme` media query. `main.tsx` applies the stored theme
 * before React mounts to avoid a flash of the wrong theme.
 */
import {
  createContext,
  use,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

export type Theme = 'light' | 'dark';

/** localStorage key — also read by `main.tsx`'s pre-render theme apply. */
export const THEME_STORAGE_KEY = 'ft-theme';

export interface ThemeState {
  theme: Theme;
  /** Switch between 'light' and 'dark'. */
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeState | null>(null);

function readStoredTheme(): Theme {
  if (typeof window === 'undefined') return 'light';
  return window.localStorage.getItem(THEME_STORAGE_KEY) === 'dark' ? 'dark' : 'light';
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setTheme] = useState<Theme>(readStoredTheme);

  useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark');
    window.localStorage.setItem(THEME_STORAGE_KEY, theme);
  }, [theme]);

  const toggleTheme = useCallback(() => {
    setTheme((current) => (current === 'dark' ? 'light' : 'dark'));
  }, []);

  const value = useMemo<ThemeState>(() => ({ theme, toggleTheme }), [theme, toggleTheme]);

  return <ThemeContext value={value}>{children}</ThemeContext>;
}

export function useTheme(): ThemeState {
  const ctx = use(ThemeContext);
  if (!ctx) {
    throw new Error('useTheme muss innerhalb von <ThemeProvider> verwendet werden.');
  }
  return ctx;
}
